/**
 * Staff UI side of the `task-events` bot (ENG-3764 D29; formerly `portal-task-lifecycle`) and the bounded poll for its
 * results (D7). No screen creates or cancels a client Task: screens save the client's QR, or stop the assignment QR
 * to cancel it (D27), and `task-events` writes the Task; `waitForTaskForQr` and `waitForTaskClosed` poll for the
 * result. The bot cannot be executed.
 */
import { badRequest, getStatus, isGone, isNotFound, OperationOutcomeError } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { CodeableConcept, OperationOutcome, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import {
  CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM,
  CHART_DOCUMENT_STATUS_ERROR,
  CHART_DOCUMENT_STATUS_EXT,
  CHART_DOCUMENT_SUPERSEDED_TAG_CODE,
  CLIENT_DOCUMENT_COMPLETION_TASK_IDENTIFIER_SYSTEM,
  CLIENT_SIGNATURE_EXT_URL,
  CLIENT_SIGNATURE_REQUEST_TASK_IDENTIFIER_SYSTEM,
  PORTAL_TASK_REFERENCE_EXT,
  ROI_CONSENT_REF_EXT,
} from '@unify/fhir-model/constants';
import {
  cancelClientAssignmentResponse,
  clientAssignmentCancelTransaction,
  getAssignedResponseReference,
  isClientAssignmentTask,
} from '@unify/fhir-model/resources/client-assignment';
import { UserFacingError } from './userFacingError';

/** D7: the screen polls once a second for up to 15 seconds. */
export const TASK_LISTENER_POLL_INTERVAL_MS = 1_000;
export const TASK_LISTENER_POLL_TIMEOUT_MS = 15_000;

/** Route id for Take Now on a Questionnaire with no Task (ENG-3764 D8): `take-now-<questionnaireId>`. */
export const TAKE_NOW_ROUTE_PREFIX = 'take-now-';

export function takeNowRouteId(questionnaireId: string): string {
  return `${TAKE_NOW_ROUTE_PREFIX}${questionnaireId}`;
}

/** The Questionnaire id of a Take Now route id, or undefined for a Task id. */
export function takeNowQuestionnaireId(routeId: string | undefined): string | undefined {
  return routeId?.startsWith(TAKE_NOW_ROUTE_PREFIX) ? routeId.slice(TAKE_NOW_ROUTE_PREFIX.length) || undefined : undefined;
}

export type TaskForQrOutcome =
  | { kind: 'created'; task: Task }
  /** The listener rejected the QR (chart status `error`). */
  | { kind: 'error'; message: string }
  /** The listener stopped the QR: the same form or assessment is already assigned and open. */
  | { kind: 'already-assigned'; message: string }
  /** Saved, but the client Task is still being created. */
  | { kind: 'timeout'; message: string };

export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  /** Test seam; defaults to `setTimeout`. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const TIMEOUT_MESSAGE = 'The assignment is saved. The client task is still being created and will appear shortly.';

function chartStatus(qr: QuestionnaireResponse): string | undefined {
  return qr.extension?.find((ext) => ext.url === CHART_DOCUMENT_STATUS_EXT)?.valueCode;
}

/** Open client Task statuses; `draft` is not open for the re-send decision or the listener wait. */
export const OPEN_KEYED_TASK_STATUSES: ReadonlySet<string> = new Set(['requested', 'received', 'accepted', 'ready', 'in-progress', 'on-hold']);

const isOpenKeyedTask = (task: Task): boolean => OPEN_KEYED_TASK_STATUSES.has(task.status ?? '');

/**
 * The keyed Task identifier systems of a chart document sent to the client: signature-request, or document-completion
 * when no client signature is required. A re-send that flips that setting keeps the open Task under the other system.
 */
export const CHART_DOCUMENT_TASK_IDENTIFIER_SYSTEMS: readonly string[] = [
  CLIENT_SIGNATURE_REQUEST_TASK_IDENTIFIER_SYSTEM,
  CLIENT_DOCUMENT_COMPLETION_TASK_IDENTIFIER_SYSTEM,
];

/** A Task `identifier` search for `<system>|<qrId>` under one or several systems (comma-joined, any match). */
function keyedTaskIdentifierSearch(identifierSystem: string | readonly string[], qrId: string): string {
  const systems = typeof identifierSystem === 'string' ? [identifierSystem] : identifierSystem;
  return systems.map((system) => `${system}|${qrId}`).join(',');
}

/**
 * ENG-3764 D3 re-sending, chart documents: true when sending this saved QR again must save a new QR instead of
 * updating it: its keyed signature-request or document-completion Task already exists and is closed (the client
 * completed it, or it was cancelled, then a reviewer reopened the document), because the listener would find that
 * closed Task and create nothing. A QR with no keyed Task (first send) or an open one keeps updating in place. A
 * `stopped` (cancelled) chart document is read-only on the chart page, so it never reaches a send.
 */
export async function chartDocumentNeedsNewQrToResend(
  medplum: MedplumClient,
  saved: QuestionnaireResponse | undefined,
): Promise<boolean> {
  if (!saved?.id) return false;
  const tasks = await medplum.searchResources(
    'Task',
    {
      identifier: keyedTaskIdentifierSearch(CHART_DOCUMENT_TASK_IDENTIFIER_SYSTEMS, saved.id),
      _count: '10',
    },
    { cache: 'no-cache' },
  );
  return tasks.length > 0 && !tasks.some(isOpenKeyedTask);
}

/**
 * Extensions that tie a chart document QR to its earlier Task, or to the content the client signed then; a re-send
 * copy starts without them.
 */
const RESEND_COPY_DROPPED_EXTENSIONS: ReadonlySet<string> = new Set([PORTAL_TASK_REFERENCE_EXT, CLIENT_SIGNATURE_EXT_URL]);

/**
 * ENG-3764 D3 re-sending: the new QR saved in place of a closed one. It keeps the answers, provider and supervisor
 * signatures and tags but no id, `authored` or other meta, and none of the original's links to its old Task or
 * lifecycle: the portal Task ref (completed, then Request Edits), a `basedOn` Task and the `superseded` lifecycle tag.
 * Otherwise the chart would resolve the new pending copy to the old Task, or hide it. It also drops the client
 * signature, which signed the earlier content: the client signs the copy afresh. The save dates the copy (`authored`
 * now). The ROI consent ref is left to the caller.
 */
export function resentChartDocumentCopy(qr: QuestionnaireResponse): QuestionnaireResponse {
  const { meta, basedOn, extension, ...rest } = qr;
  delete rest.id;
  delete rest.authored;
  const keptBasedOn = basedOn?.filter((ref) => !ref.reference?.startsWith('Task/'));
  const keptExtension = extension?.filter((e) => !RESEND_COPY_DROPPED_EXTENSIONS.has(e.url));
  const keptTag = meta?.tag?.filter((tag) => tag.system !== CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM);
  return {
    ...rest,
    ...(keptTag?.length ? { meta: { tag: keptTag } } : {}),
    ...(keptBasedOn?.length ? { basedOn: keptBasedOn } : {}),
    ...(keptExtension?.length ? { extension: keptExtension } : {}),
  };
}

/**
 * The original retired by a re-send: the D27 stop (keeps the template tag), no ROI consent ref, and the `superseded`
 * lifecycle tag when the copy was saved. The extra tag leaves the D25 `writeConstraint` satisfied: it requires only
 * the assignment tag on the new version.
 */
function retiredResentOriginal(qr: QuestionnaireResponse, copyId: string | undefined): QuestionnaireResponse {
  const stopped = cancelClientAssignmentResponse(qr);
  const tag = (stopped.meta?.tag ?? []).filter((t) => t.system !== CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM);
  if (copyId) tag.push({ system: CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM, code: CHART_DOCUMENT_SUPERSEDED_TAG_CODE });
  return {
    ...stopped,
    meta: { ...stopped.meta, tag },
    extension: (stopped.extension ?? []).filter((e) => e.url !== ROI_CONSENT_REF_EXT),
  };
}

function isPreconditionFailed(err: unknown): boolean {
  return err instanceof OperationOutcomeError && getStatus(err.outcome) === 412;
}

/**
 * ENG-3764 D3 re-sending: once the copy exists, retire the original (chart status `cancelled`, QR `stopped`, the
 * `superseded` lifecycle tag) so the chart shows one document. The original also drops its ROI consent ref: the
 * copy is the document the client can still sign, so a revoke of that Consent must find the copy. The write is built
 * from a fresh read and sent with If-Match (one re-read and retry on 412), so it never overwrites a concurrent save,
 * and it keeps the template-category tags, the portal `forms` tag included: the D25 `writeConstraint` that admits
 * this write for practice admin, practice assistant and unify roles requires the tag on the new version. Skipped
 * when the original is already `stopped`. Callers run it after the copy was saved (a failed create never loses the
 * original) and treat a failure as a warning. Returns whether it wrote.
 */
export async function supersedeResentChartDocument(
  medplum: MedplumClient,
  original: QuestionnaireResponse | undefined,
  copyId: string | undefined,
): Promise<boolean> {
  if (!original?.id || original.status === 'stopped') return false;
  for (let attempt = 0; ; attempt += 1) {
    const fresh = await medplum.readResource('QuestionnaireResponse', original.id, { cache: 'no-cache' });
    if (fresh.status === 'stopped') return false;
    const versionId = fresh.meta?.versionId;
    try {
      await medplum.updateResource<QuestionnaireResponse>(
        retiredResentOriginal(fresh, copyId),
        versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined,
      );
      return true;
    } catch (err) {
      if (attempt > 0 || !isPreconditionFailed(err)) throw err;
    }
  }
}

async function poll<T>(options: PollOptions | undefined, attempt: () => Promise<T | undefined>, onTimeout: () => T): Promise<T> {
  const intervalMs = options?.intervalMs ?? TASK_LISTENER_POLL_INTERVAL_MS;
  const timeoutMs = options?.timeoutMs ?? TASK_LISTENER_POLL_TIMEOUT_MS;
  const sleep = options?.sleep ?? defaultSleep;
  const attempts = Math.max(1, Math.floor(timeoutMs / intervalMs));
  for (let i = 0; i < attempts; i += 1) {
    const result = await attempt();
    if (result !== undefined) return result;
    if (i < attempts - 1) await sleep(intervalMs);
  }
  return onTimeout();
}

/** What the QR itself says the listener did: rejected it (chart status `error`) or stopped it as a duplicate. */
function listenerQrVerdict(qr: QuestionnaireResponse): TaskForQrOutcome | undefined {
  if (chartStatus(qr) === CHART_DOCUMENT_STATUS_ERROR) {
    return { kind: 'error', message: 'This assignment could not be sent to the client.' };
  }
  if (qr.status === 'stopped') {
    return { kind: 'already-assigned', message: 'This form is already assigned and open for the client.' };
  }
  return undefined;
}

/**
 * D7 QR flows: after saving a `pending-client` QR, wait for the listener's Task, found by the D3 identifier
 * `<identifierSystem>|<qrId>` (for a chart document, `CHART_DOCUMENT_TASK_IDENTIFIER_SYSTEMS`, since an in-place
 * re-send keeps its open Task). Checks, in order:
 * chart status `error`, QR `stopped`, an open Task exists (a closed one is an error, once a re-read of the QR shows it
 * was not stopped or rejected meanwhile); otherwise times out with a warning, not an error.
 */
export async function waitForTaskForQr(
  medplum: MedplumClient,
  qrId: string,
  identifierSystem: string | readonly string[],
  options?: PollOptions,
): Promise<TaskForQrOutcome> {
  return poll<TaskForQrOutcome>(
    options,
    async () => {
      const verdict = listenerQrVerdict(await medplum.readResource('QuestionnaireResponse', qrId, { cache: 'no-cache' }));
      if (verdict) return verdict;
      const tasks = await medplum.searchResources(
        'Task',
        { identifier: keyedTaskIdentifierSearch(identifierSystem, qrId), _count: '10' },
        { cache: 'no-cache' },
      );
      const open = tasks.find(isOpenKeyedTask);
      if (open) return { kind: 'created', task: open };
      // The identifier is unique per QR, so a closed match means no new Task is coming: the listener found an old one.
      if (tasks.length > 0) {
        // Unless the listener withdrew this QR as a concurrent duplicate after the read above: it stops the QR, then
        // cancels the Task it created for it. Re-read the QR so that race reports the stop, not a closed Task.
        return listenerQrVerdict(await medplum.readResource('QuestionnaireResponse', qrId, { cache: 'no-cache' })) ?? {
          kind: 'error',
          message: 'This document was already sent and its task is completed or cancelled. Send it again as a new document.',
        };
      }
      return undefined;
    },
    () => ({ kind: 'timeout', message: TIMEOUT_MESSAGE }),
  );
}

export type AssignmentCancelOutcome =
  | { kind: 'cancelled'; task: Task }
  /** The Task closed some other way (`rejected`, `failed`, `entered-in-error`): it was not cancelled. */
  | { kind: 'closed'; task: Task; message: string }
  /** Not cancelled yet: the QR is stopped (or deleted) and `task-events` has not closed the Task (the Subscription retries). */
  | { kind: 'timeout'; message: string };

export const ASSIGNMENT_CANCEL_TIMEOUT_MESSAGE = 'The request is cancelled. The client task will close shortly.';

/** A legacy client Task (before ENG-3764) names no assignment QR, and no user edits a Task (D29). */
export const LEGACY_TASK_CANCEL_MESSAGE =
  'This request was created before requests were tracked by their form, so it cannot be cancelled here. Contact support to close it.';

/**
 * D27: what a stored Task says about a cancel. `cancelled` → done; `completed` (the client submitted first) → an
 * error, since the assignment was not cancelled; any other closed status → `closed`, also not a cancel; still open
 * (or `draft`) → undefined.
 */
function closedTaskCancelOutcome(task: Task): AssignmentCancelOutcome | undefined {
  if (isOpenKeyedTask(task) || task.status === 'draft') return undefined;
  if (task.status === 'completed') throw new UserFacingError('The client completed this request before it was cancelled.');
  if (task.status === 'cancelled') return { kind: 'cancelled', task };
  return { kind: 'closed', task, message: `This request was already closed (${task.status ?? 'unknown status'}), so it was not cancelled.` };
}

/** D27: poll until the Task is no longer open (`closedTaskCancelOutcome`); still open at the limit → a timeout warning. */
export async function waitForTaskClosed(
  medplum: MedplumClient,
  taskId: string,
  options?: PollOptions,
): Promise<AssignmentCancelOutcome> {
  return poll<AssignmentCancelOutcome>(
    options,
    async () => closedTaskCancelOutcome(await medplum.readResource('Task', taskId, { cache: 'no-cache' })),
    () => ({ kind: 'timeout', message: ASSIGNMENT_CANCEL_TIMEOUT_MESSAGE }),
  );
}

function transactionEntryOutcome(outcome: OperationOutcome | undefined, status: string | undefined): OperationOutcome {
  return outcome ?? badRequest(`The cancel was not saved (${status ?? 'no response'}).`);
}

/**
 * D27: the staff stop of a pending assignment QR and its cancel Provenance (`agent.who` the signed-in staff member,
 * `reason` the one they gave), in one transaction (`clientAssignmentCancelTransaction`), so the stopped-QR listener
 * finds the reason when it cancels the Task. The stop carries If-Match, so a submission that lands first fails the
 * whole transaction (412) and nothing is written. The stored stopped QR is returned. With no signed-in profile it
 * writes the stop alone and the listener uses a generic reason.
 */
export async function stopClientAssignmentQr(
  medplum: MedplumClient,
  qr: QuestionnaireResponse,
  reason?: CodeableConcept,
): Promise<QuestionnaireResponse> {
  const profile = medplum.getProfile();
  if (!profile?.id) {
    const versionId = qr.meta?.versionId;
    return medplum.updateResource<QuestionnaireResponse>(
      cancelClientAssignmentResponse(qr),
      versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined,
    );
  }
  const agent = { reference: `${profile.resourceType}/${profile.id}` };
  const response = await medplum.executeBatch(clientAssignmentCancelTransaction(qr, agent, reason, new Date().toISOString()));
  // A transaction fails as a whole; check the stop's own entry too, in case the server ran it as a batch.
  const stop = response.entry?.[0];
  if (!stop?.response?.status?.startsWith('2')) {
    throw new OperationOutcomeError(transactionEntryOutcome(stop?.response?.outcome as OperationOutcome | undefined, stop?.response?.status));
  }
  return (stop.resource as QuestionnaireResponse | undefined) ?? cancelClientAssignmentResponse(qr);
}

async function readAssignmentQr(medplum: MedplumClient, qrId: string): Promise<QuestionnaireResponse | undefined> {
  try {
    return await medplum.readResource('QuestionnaireResponse', qrId, { cache: 'no-cache' });
  } catch (err) {
    if (err instanceof OperationOutcomeError && (isNotFound(err.outcome) || isGone(err.outcome))) return undefined;
    throw err;
  }
}

/**
 * D27: cancel a client assignment Task (`isClientAssignmentTask`) the way staff may: stop its assignment QR (status
 * `stopped`, chart status `cancelled`) with If-Match and its cancel Provenance carrying the reason, in one transaction
 * (`stopClientAssignmentQr`), then wait for `task-events` to cancel the Task. A QR that was already stopped (in any
 * way) or deleted is only waited on: `task-events` closes the Task of a stopped or deleted assignment QR (D29), so
 * there is no direct Task cancel. A QR the client already submitted cannot be cancelled. The staff AccessPolicy
 * decides the QR write (D25/D26).
 */
export async function cancelClientAssignment(
  medplum: MedplumClient,
  task: Task,
  reason?: CodeableConcept,
  options?: PollOptions,
): Promise<AssignmentCancelOutcome> {
  const qrId = getAssignedResponseReference(task)?.reference
    ?.replace(/^QuestionnaireResponse\//, '');
  if (!task.id || !qrId) throw new UserFacingError('This request has no assignment to cancel.');
  const qr = await readAssignmentQr(medplum, qrId);
  if (qr?.status === 'completed' || qr?.status === 'amended') {
    throw new UserFacingError('The client already submitted this request, so it can no longer be cancelled.');
  }
  if (qr && qr.status !== 'stopped') await stopClientAssignmentQr(medplum, qr, reason);
  medplum.invalidateSearches('QuestionnaireResponse');
  const outcome = await waitForTaskClosed(medplum, task.id, options);
  medplum.invalidateSearches('Task');
  return outcome;
}

/**
 * Cancel a client Task from a staff screen by stopping its QR (D27): a client assignment (`isClientAssignmentTask`)
 * through its assignment QR. A legacy Task from before ENG-3764 names no QR and is not cancelled (D29: no user edits
 * a Task).
 */
export async function cancelClientTask(
  medplum: MedplumClient,
  task: Task,
  reason?: CodeableConcept,
  options?: PollOptions,
): Promise<AssignmentCancelOutcome> {
  if (!isClientAssignmentTask(task)) throw new UserFacingError(LEGACY_TASK_CANCEL_MESSAGE);
  return cancelClientAssignment(medplum, task, reason, options);
}
